import React from 'react';
import { Modal, ModalHeader, ModalBody, Table, Badge } from 'reactstrap';
import moment from 'moment';

const statusColor = (s) => ({
    APPROVED: 'success', PENDING: 'warning', REJECTED: 'danger',
    DELETED: 'danger', DELETION_REQUESTED: 'warning',
}[s] ?? 'secondary');

const formatDate = (d) => (d ? moment(d).format('D MMM YYYY, HH:mm') : '-');

const VesselViewModal = ({ isOpen, toggle, vessel }) => {
    if (!vessel) return null;

    const now = moment();

    // Events come newest first. The DELETED event names the admin who removed
    // the vessel, whether on the customer's request or on their own initiative.
    const events = vessel.status_events ?? [];
    const removedEvent = events.find(e => e.event === 'DELETED');
    // Only the outstanding request: an earlier, declined one also left an event.
    const requestedEvent = vessel.deletion_requested_at ? events.find(e => e.event === 'DELETION_REQUESTED') : null;
    const awaitingRemoval = !!vessel.deletion_requested_at && !vessel.is_deleted;

    return (
        <Modal isOpen={isOpen} toggle={toggle} size="lg">
            <ModalHeader toggle={toggle}>{vessel.name}</ModalHeader>
            <ModalBody>
                <h6 className="text-muted mb-2">Vessel Details</h6>
                <Table bordered size="sm" className="mb-4">
                    <tbody>
                        <tr><th style={{ width: '35%' }}>Registration No.</th><td>{vessel.registration_number}</td></tr>
                        <tr><th>Status</th><td>
                            <Badge color={statusColor(vessel.status)}>{vessel.status}</Badge>
                            {vessel.is_deleted ? <Badge color="danger" className="ms-2">DELETED</Badge> : null}
                            {awaitingRemoval ? <Badge color="warning" className="ms-2">DELETION REQUESTED</Badge> : null}
                        </td></tr>
                        <tr><th>Vessel Type</th><td>{vessel.vessel_type?.name ?? '-'}</td></tr>
                        <tr><th>Owner</th><td>{vessel.owner?.name} ({vessel.owner?.email})</td></tr>
                        {vessel.owner_company_name && (
                            <tr><th>Company</th><td>{vessel.owner_company_name}</td></tr>
                        )}
                        <tr><th>Representative</th><td>{vessel.representative_name} — {vessel.representative_contact_number}</td></tr>
                        <tr><th>Length / Breadth</th><td>{vessel.length}m / {vessel.breadth}m</td></tr>
                        <tr><th>Gross Tonnage</th><td>{vessel.gross_tonnage}</td></tr>
                        <tr><th>Approved By</th><td>{vessel.approver ? `${vessel.approver.name} on ${vessel.approved_at_label}` : '-'}</td></tr>
                        {vessel.rejection_reason && (
                            <tr><th>Rejection Reason</th><td className="text-danger">{vessel.rejection_reason}</td></tr>
                        )}
                    </tbody>
                </Table>

                {(awaitingRemoval || vessel.is_deleted) && (
                    <>
                        <h6 className="text-muted mb-2">Removal</h6>
                        <Table bordered size="sm" className="mb-4">
                            <tbody>
                                {vessel.deletion_requested_at ? (
                                    <tr>
                                        <th style={{ width: '35%' }}>Requested By</th>
                                        <td>{requestedEvent?.actor_name ?? vessel.owner?.name ?? '-'} on {formatDate(requestedEvent?.created_at ?? vessel.deletion_requested_at)}</td>
                                    </tr>
                                ) : (
                                    <tr><th style={{ width: '35%' }}>Requested By</th><td>Removed by staff, not requested by the customer</td></tr>
                                )}
                                <tr>
                                    <th>Reason</th>
                                    <td>{removedEvent?.reason ?? vessel.deletion_reason ?? 'No reason given'}</td>
                                </tr>
                                {vessel.is_deleted && (
                                    <tr>
                                        <th>{vessel.deletion_requested_at ? 'Approved & Deleted By' : 'Deleted By'}</th>
                                        <td>
                                            {removedEvent?.actor_name ?? 'Not recorded'}
                                            {' on '}{formatDate(removedEvent?.created_at ?? vessel.deleted_at)}
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </Table>
                    </>
                )}

                <h6 className="text-muted mb-2">Permits ({vessel.permits?.length ?? 0})</h6>
                {vessel.permits && vessel.permits.length > 0 ? (
                    <Table bordered size="sm" className="mb-4">
                        <thead>
                            <tr><th>#</th><th>Type</th><th>Effective</th><th>Expires</th><th>Status</th></tr>
                        </thead>
                        <tbody>
                            {vessel.permits.map((p, i) => {
                                const active = moment(p.effective_at).isBefore(now) && moment(p.expires_at).isAfter(now);
                                const upcoming = moment(p.effective_at).isAfter(now);
                                return (
                                    <tr key={p.id}>
                                        <td>{i + 1}</td>
                                        <td>{p.permit_type?.name ?? '-'}</td>
                                        <td>{p.permit_effective_display}</td>
                                        <td>{p.permit_expiry_display}</td>
                                        <td>
                                            <Badge color={active ? 'success' : upcoming ? 'warning' : 'secondary'}>
                                                {active ? 'Active' : upcoming ? 'Upcoming' : 'Expired'}
                                            </Badge>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </Table>
                ) : (
                    <p className="text-muted">No permits on record.</p>
                )}

                <h6 className="text-muted mb-2">Status History ({vessel.status_events?.length ?? 0})</h6>
                {vessel.status_events && vessel.status_events.length > 0 ? (
                    <Table bordered size="sm" className="mb-0">
                        <thead>
                            <tr><th>Event</th><th>By</th><th>Date</th><th>Reason</th></tr>
                        </thead>
                        <tbody>
                            {vessel.status_events.map(e => (
                                <tr key={e.id}>
                                    <td><Badge color={statusColor(e.event)}>{e.event.replace(/_/g, ' ')}</Badge></td>
                                    <td>{e.actor_name ?? '-'}</td>
                                    <td>{formatDate(e.created_at)}</td>
                                    <td>{e.reason ?? '-'}</td>
                                </tr>
                            ))}
                        </tbody>
                    </Table>
                ) : (
                    <p className="text-muted mb-0">No status events.</p>
                )}
            </ModalBody>
        </Modal>
    );
};

export default VesselViewModal;
